import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { formatPhoneNumber } from '@utils/format'
import _ from 'lodash'
import {
  fetchUserStoreManagementDetail,
  fetchUserStoreManagementList,
  searchUserStoreManagementList,
  sendCredentialGenRequest
} from './asyncAction'
import { initialState } from './initState'

const userStoreManagementSlice = createSlice({
  name: 'userStoreManagement',
  initialState,
  reducers: {
    resetUserStoreList (state) {
      state.userStoreList = state.originalUserStoreList
    },
    resetSearchList (state) {
      state.searchUserStoreList = state.searchOriginalUserStoreList
      state.isSearchingUserStoreList = false
    },
    pushToStoresAdded (state, action: PayloadAction<TUserStoreManagementDetail.TState['storesAdded']>) {
      state.storesAdded = action.payload
    },
    removeStoreAddedByIndex (state, action: PayloadAction<number>) {
      state.storesAdded.splice(action.payload, 1)
    },
    removeStoreAddedById (state, action: PayloadAction<TUserStoreManagementDetail.TState['storesAdded'][0]>) {
      state.storesAdded = state.storesAdded.filter(({ storeId }) => storeId === action.payload.storeId)
    },
    mapSearchWithCurrentStoreAdded (state, action: PayloadAction<TUserStoreManagementDetail.TState['searchUserStoreList']['data']>) {
      const storeidsAdded = action.payload.map(({ storeId }) => storeId)
      state.searchUserStoreList.data = state.searchUserStoreList.data.map(item => ({
        ...item,
        isChecked: storeidsAdded.includes(item.storeId)
      }))
    },
    setFieldInUserStoreManagementDetail (
      state,
      action: PayloadAction<{ value: any, field: keyof TUserStoreManagementDetail.TState['userStoreList']['data'][0] }>
    ) {
      const { value, field } = action.payload
      // @ts-ignore
      state.userStoreManagementDetail.data[field] = value
    },
    setChecked (state, action: PayloadAction<{ value: boolean, index: number }>) {
      const { index, value } = action.payload
      state.searchUserStoreList.data[index].isChecked = value
    },
    setShowCredentialGeneratorModel (state, action) {
      state.showCredentialGeneratorModel = action.payload
    },
    setLoading (state, action) {
      state.isLoading = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUserStoreManagementList.fulfilled, (
        state,
        action: PayloadAction<TUserStoreManagementDetail.TState['userStoreList']>
      ) => {
        if (!action.payload?.data) return
        state.userStoreList = action.payload
        if (!state.originalUserStoreList.data.length) {
          state.originalUserStoreList = action.payload
        }
        state.isFetchingUserStoreList = false
      })
      .addCase(fetchUserStoreManagementList.pending, state => {
        state.isFetchingUserStoreList = true
      })
    builder
      .addCase(searchUserStoreManagementList.fulfilled, (
        state,
        action: PayloadAction<TUserStoreManagementDetail.TState['searchUserStoreList']>
      ) => {
        if (!action.payload?.data) return
        const storeidsAdded = state.storesAdded.map(({ storeId }) => storeId)
        const listWithCheckbox = {
          ...action.payload,
          data: action.payload.data.map(item => ({
            ...item,
            isChecked: storeidsAdded.includes(item.storeId)
          }))
        }
        state.searchUserStoreList = listWithCheckbox
        if (!state.searchOriginalUserStoreList.data.length) {
          state.searchOriginalUserStoreList = listWithCheckbox
        }
        state.isSearchingUserStoreList = false
      })
      .addCase(searchUserStoreManagementList.pending, state => {
        state.isFetchingUserStoreList = true
      })
    builder
      .addCase(fetchUserStoreManagementDetail.fulfilled, (
        state,
        action: PayloadAction<TUserStoreManagementDetail.TState['userStoreManagementDetail']>
      ) => {
        if (!action.payload?.data) return
        state.storesAdded = []
        const newVal = action.payload.data.phonenumber
          ? _.set(action.payload, 'data.phoneNumber', formatPhoneNumber(action.payload.data.phonenumber))
          : action.payload
        state.userStoreManagementDetail = newVal
        state.isFetchingUserStoreDetail = false
      })
      .addCase(fetchUserStoreManagementDetail.pending, state => {
        state.isFetchingUserStoreDetail = true
      })
      .addCase(sendCredentialGenRequest.fulfilled, state => {
        state.isLoading = false
      })
  }
})

export * from './asyncAction'

export const {
  resetUserStoreList,
  setFieldInUserStoreManagementDetail,
  setChecked,
  setShowCredentialGeneratorModel,
  resetSearchList,
  mapSearchWithCurrentStoreAdded,
  pushToStoresAdded,
  removeStoreAddedByIndex,
  removeStoreAddedById,
  setLoading
} = userStoreManagementSlice.actions

export const userStoreManagementReducer = userStoreManagementSlice.reducer
